import { useMutation, useQuery } from "@tanstack/react-query";
import { ApiService } from "@/services/api/ApiService";
import { WidgetCommunicationService } from "@/services/integrations/widget/WidgetCommunicationService";
import type { AskByEmailValues, EmailWidgetApiError, EmailWidgetConfig } from "@/types/ask-by-email";

export const useEmailWidget = (slug: string | undefined, source: string | null) => {
  const config = useQuery({
    queryKey: ["email-widget", slug],
    enabled: !!slug,
    retry: false,
    queryFn: () => ApiService.invoke<EmailWidgetConfig>({
      resource: "email-widget",
      action: "get",
      data: { url_name: slug },
    }, "public"),
  });

  const send = useMutation({
    retry: false,
    mutationFn: async (values: AskByEmailValues) => {
      try {
        await ApiService.invoke<{ accepted: true }>({
          resource: "email-widget",
          action: "send",
          data: { url_name: slug, ...values, source },
        }, "public");
      } catch (error) {
        // Public handler messages are deliberately visitor-safe. Preserve them
        // when the transport wraps a non-2xx response in FunctionsHttpError.
        const response = (error as EmailWidgetApiError)?.context;
        if (response instanceof Response) {
          const body = await response.clone().json().catch(() => null);
          if (typeof body?.error?.message === "string") throw new Error(body.error.message);
        }
        throw new Error("We couldn’t confirm delivery. Check your inbox before trying again.");
      }
    },
  });

  return {
    config,
    send: send.mutateAsync,
    close: () => WidgetCommunicationService.messages.close(),
  };
};
